import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RemoveOutpatientModal from "./modals/RemoveOutpatient";
import Loader from "../../common/Loader/Loader";
import APIVariables from "../../../services/api-variables";
import { handleCatch } from "../../../shared/helpers";
import { errorMessages } from "../../../constants/errorMessages.constants";
import { App } from "../../../routes/Constants";
import Axios from "../../../services/Axios";
import DataTable from "@/global/tables/DataTable";
import { getConnectedOutpatientsColumns } from "./ConnectedOutpatientsColumns";

const ConnectedOutpatients = () => {
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOutpatient, setSelectedOutpatient] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [items, setItems] = useState([]);

  const navigate = useNavigate();

  const addOutpatient = () => {
    navigate(App.CONNECTED_OUTPATIENT_ADD);
  };

  const fetchEntireOutpatientList = async () => {
    setIsLoading(true);
    try {
      let acc = [];
      let nextUrl = APIVariables.HCP_OUTPATIENTS;

      while (nextUrl) {
        const res = await Axios.get(nextUrl);
        if (res.status !== 200) break;

        const { data } = res;

        if (Array.isArray(data)) {
          acc = data;
          nextUrl = null;
        } else {
          const chunk = data?.results ?? [];
          acc = acc.concat(chunk);
          nextUrl = data?.next || null;
        }
      }

      setItems(acc);
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntireOutpatientList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModalRemoveOutpatient = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedOutpatient(id);
    setIsModalOpen(true);
  }, []);

  const redirectToEditOutpatient = useCallback(
    (row) => {
      navigate(App.CONNECTED_OUTPATIENT_EDIT_DEFAULT + `${row.connected_user_id}`);
    },
    [navigate]
  );

  const closeModal = () => {
    setSelectedOutpatient("");
    setErrorMessage("");
    setIsModalOpen(false);
  };

  const removeOutpatient = async () => {
    setErrorMessage("");
    await Axios.delete(APIVariables.HCP_OUTPATIENT_DELETE + selectedOutpatient)
      .then((res) => {
        const { status } = res;
        if (status === 200) {
          const newItems = items.filter((item) => {
            return item.connected_user_id !== selectedOutpatient;
          });
          setItems(newItems);
          closeModal();
        }
      })
      .catch(function (error) {
        const errors = error.response.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          Object.keys(errors).forEach((key) => {
            errorsArray.push(key + " - " + errors[key]);
          });
          setErrorMessage(errorsArray);
        }
      });
  };

  const columns = useMemo(
    () =>
      getConnectedOutpatientsColumns({
        onRemove: openModalRemoveOutpatient,
      }),
    [openModalRemoveOutpatient]
  );

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="connected-outpatients-page"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="mx-auto max-w-[1100px] space-y-4">
          <div className="flex items-center justify-between py-2.5">
            <h1
              className="h1 mineShaft-color"
              data-testid="connected-outpatients-heading"
            >
              Connected Outpatient Pharmacy
            </h1>
            <button
              type="button"
              className="second-button"
              onClick={addOutpatient}
              data-testid="connected-outpatients-add-button"
            >
              Add Outpatient
            </button>
          </div>
          <div
            className="rounded-md border bg-white px-4 pb-4"
            data-testid="connected-outpatients-table"
          >
            <DataTable
              columns={columns}
              data={items}
              tableTitle="Connected outpatient pharmacy"
              onRowClick={redirectToEditOutpatient}
              loading={false}
              getRowTestId={(row) =>
                `connected-outpatients-row-${row?.connected_user_id}`
              }
            />
          </div>
          {modalIsOpen && (
            <RemoveOutpatientModal
              modalIsOpen={modalIsOpen}
              closeModal={closeModal}
              removeOutpatient={removeOutpatient}
              errorMessage={errorMessage}
            />
          )}
        </div>
      )}
    </div>
  );
};
export default ConnectedOutpatients;
